/**
 * Spatial Indexing Integration for Visual Feedback System
 *
 * NOTE: Spatial indexing has been removed as TerraMatrix is now AI-chat-only.
 * This file provides stub implementations for backward compatibility.
 * All components use flex-based layout, eliminating the need for viewport culling.
 */

import {
  VisualFeedbackContext,
  SpatialIndexingOptimization,
} from "./types";
import { Position, ComponentType } from "@/types";
import { Rectangle } from "@/types/canvas-legacy-types";

/**
 * Spatial-aware visual feedback optimization (STUB)
 *
 * NOTE: This is now a stub implementation. Spatial indexing has been removed
 * as TerraMatrix uses AI-chat-only interaction with flex-based layouts.
 */
export class SpatialIndexingIntegration {
  private config: SpatialIndexingOptimization;
  private visibleComponents: Set<string> = new Set();

  constructor(config: Partial<SpatialIndexingOptimization> = {}) {
    this.config = {
      enabled: false, // Disabled by default - no spatial indexing
      cullingThreshold: 1000,
      viewportCulling: false,
      performanceScaling: false,
      lodSystem: false,
      ...config,
    };
  }

  /**
   * Update viewport bounds (no-op in stub)
   */
  public updateViewportBounds(_bounds: Rectangle): void {
    // No-op: Viewport culling removed
  }

  /**
   * Register component (stub - just tracks component ID)
   */
  public registerComponent(
    componentId: string,
    _bounds: Rectangle,
    _componentType: ComponentType,
    _zIndex: number = 0,
  ): void {
    this.visibleComponents.add(componentId);
  }

  /**
   * Update component position (no-op in stub)
   */
  public updateComponentPosition(
    _componentId: string,
    _newBounds: Rectangle,
    _zIndex?: number,
  ): void {
    // No-op: Spatial indexing removed
  }

  /**
   * Remove component from tracking
   */
  public removeComponent(componentId: string): void {
    this.visibleComponents.delete(componentId);
  }

  /**
   * Optimize visual feedback context (pass-through in stub)
   */
  public optimizeContext(
    context: VisualFeedbackContext,
  ): VisualFeedbackContext {
    // Return context unchanged - all components visible in flex layout
    return {
      ...context,
      isVisible: true,
      performanceLevel: "high",
    };
  }

  /**
   * Get nearby components (returns empty array - no spatial queries in flex)
   */
  public getNearbyComponents(
    _position: Position,
    _radius: number,
    _excludeId?: string,
  ): string[] {
    return [];
  }

  /**
   * Detect collisions (returns empty array - flex prevents overlaps)
   */
  public detectCollisions(_componentId: string, _bounds: Rectangle): string[] {
    return [];
  }

  /**
   * Get optimization statistics (stub values)
   */
  public getOptimizationStats() {
    return {
      totalComponents: this.visibleComponents.size,
      visibleComponents: this.visibleComponents.size,
      culledComponents: 0,
      lodLevels: {
        high: this.visibleComponents.size,
        medium: 0,
        low: 0,
      },
      performanceMetrics: {
        frameRate: 60,
        memoryUsage: 0,
        cpuUsage: 0,
      },
    };
  }

  /**
   * Enable or disable optimizations (no-op in stub)
   */
  public setOptimizationEnabled(
    _optimization: keyof SpatialIndexingOptimization,
    _enabled: boolean,
  ): void {
    // No-op: Spatial optimizations removed
  }

  /**
   * Force refresh (no-op in stub)
   */
  public refresh(): void {
    // No-op: No spatial index to refresh
  }

  /**
   * Dispose of resources
   */
  public dispose(): void {
    this.visibleComponents.clear();
  }
}
